import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/shadcn-card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Star, Trophy, Users, Calendar, ArrowRight, CheckCircle } from 'lucide-react';

const ShadcnDemo = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Header Section */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-white mb-2">
            🎉 Your Site with shadcn/ui
          </h1>
          <p className="text-xl text-slate-300">
            See how much better your components can look!
          </p>
        </div>

        {/* Button Showcase */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              Beautiful Buttons
            </CardTitle>
            <CardDescription>
              Multiple variants with consistent styling and accessibility
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button>Default Button</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
              <Button size="icon">
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cards Showcase */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-colors">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Challenge Stats
                </CardTitle>
                <Badge variant="secondary">Live</Badge>
              </div>
              <CardDescription>
                Real-time competition metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Participants</span>
                  <span className="text-white font-medium">1,247</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Submissions</span>
                  <span className="text-white font-medium">3,891</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Prize Pool</span>
                  <span className="text-yellow-500 font-medium">$50,000</span>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full">
                <Users className="h-4 w-4 mr-2" />
                Join Challenge
              </Button>
            </CardFooter>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-colors">
            <CardHeader>
              <CardTitle className="text-white">Quick Actions</CardTitle>
              <CardDescription>
                Common tasks made easy
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input placeholder="Search challenges..." className="bg-slate-900/50 border-slate-600" />
              <div className="flex gap-2">
                <Badge>AI/ML</Badge>
                <Badge variant="outline">Computer Vision</Badge>
                <Badge variant="secondary">NLP</Badge>
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button variant="outline" size="sm">Filter</Button>
              <Button size="sm">Search</Button>
            </CardFooter>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-colors">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Upcoming Events
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Alert className="bg-blue-900/20 border-blue-800">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription className="text-blue-200">
                  New challenge starts in 2 days!
                </AlertDescription>
              </Alert>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Workshop</span>
                  <span className="text-white">Dec 15</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Deadline</span>
                  <span className="text-red-400">Dec 20</span>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full">
                    View Details
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-800 border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Event Details</DialogTitle>
                    <DialogDescription>
                      Learn more about upcoming events and deadlines.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-slate-300">
                      Join our upcoming AI workshop where industry experts will share
                      insights on the latest machine learning techniques.
                    </p>
                    <div className="flex gap-2">
                      <Button>Register Now</Button>
                      <Button variant="outline">Add to Calendar</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardFooter>
          </Card>
        </div>

        {/* Benefits Section */}
        <Card className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-purple-700">
          <CardHeader>
            <CardTitle className="text-white text-2xl">
              ✨ Benefits of shadcn/ui for Your Project
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Design Benefits</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Consistent, professional appearance
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Built-in dark mode support
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Smooth animations and transitions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Responsive design out of the box
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Developer Benefits</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Fully accessible components (ARIA)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    TypeScript support (optional)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Copy-paste approach - you own the code
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Easy customization with CSS variables
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Migration Guide */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">🚀 How to Start Using shadcn/ui</CardTitle>
            <CardDescription>
              Replace your existing components gradually
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-slate-900/50 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">1. Import the new components:</h4>
                <code className="text-green-400 text-sm">
                  {`import { Button } from '@/components/ui/button';`}
                </code>
              </div>
              <div className="bg-slate-900/50 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">2. Replace your old buttons:</h4>
                <code className="text-green-400 text-sm">
                  {`<Button variant="outline" size="lg">Click me</Button>`}
                </code>
              </div>
              <div className="bg-slate-900/50 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">3. Add more components as needed:</h4>
                <code className="text-green-400 text-sm">
                  {`npx shadcn@latest add dropdown-menu tabs tooltip`}
                </code>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default ShadcnDemo;